import { capitalizeFirst } from 'ramda-extension';
import { events } from '@waybetter/wb-analytics';

import { logger } from '#src/lib/logger';
import Link from '#src/components/Link';
import { completeGameOrder } from '#src/lib/order/completeGameOrder';
import { register } from '#src/lib/auth';
import { createToast } from '#src/store/toasts/actions';
import { clearProspectUser, setSessionUser } from '#src/store/session/actions';

export async function handleSubmit(
  { storeInVault, email, password },
  {
    setSubmitting,
    props: {
      geo,
      bets = [],
      dispatch,
      theme,
      games = [],
      setPaymentError,
      order: {
        id: orderId,
        amountDue,
        userId,
        requiresPaymentMethod,
        requiresStoreInVault,
      },
      discounts = [],
      memberships = [],
      braintree,
      completeOrder: mutation,
      membershipReturnGame,
      timeZone,
      createNotification,
      setMembershipReturnGame,
      SUPPORT_EMAIL,
      refreshOrder,
      requiresRegistration,
      isAdHocMembershipOrder,
      referringGameId,
      user,
    },
  }
) {
  let response = {};

  try {
    if (requiresPaymentMethod) {
      response = await braintree.requestPaymentMethod();
    }
  } catch (error) {
    logger.error(error, { message: 'Failed to request payment nonce.' });
    setSubmitting(false);
    setPaymentError(error);
    return;
  }

  try {
    if (requiresRegistration) {
      const data = await register(
        {
          userId: user.id,
          firstName: user.firstName,
          lastName: user.lastName,
          email,
          clearTextPassword: password,
        },
        { product: theme }
      );

      // If the prospect changed their email during check out, track it
      if (email !== user.email) {
        analytics.track(
          // Differentiate between success and error events
          `Prospect Email Change${data.error ? ' Error' : ''}`,
          {
            original: user.email,
            new: email,
            message: data?.error?.errorMessage,
            prospect_user_id: user.id,
          }
        );
      }

      if (data.error) {
        throw new Error(data.error.errorMessage);
      }

      dispatch(setSessionUser(data.user));
      dispatch(clearProspectUser());

      analytics.track(events.SIGNED_UP, { method: 'email' });
      analytics.track(events.PROSPECT_COMPLETED_REGISTRATION, {
        category: theme,
        email,
        prospect_user_id: user.id,
      });
    }
  } catch (error) {
    logger.error(error, { message: 'Failed to complete user registration.' });
    dispatch(
      createToast({
        message: error,
        status: 'error',
      })
    );
    setSubmitting(false);
    return;
  }

  try {
    await completeGameOrder({
      bets,
      discounts,
      games,
      isAdHocMembershipOrder,
      membershipReturnGame,
      memberships,
      mutation,
      nonce: response.nonce,
      orderId,
      payAmount: amountDue,
      requiresRegistration,
      referringGameId,
      setMembershipReturnGame,
      storeInVault: storeInVault || requiresStoreInVault,
      theme,
      timeZone,
      user,
      userId,
      geo,
    });
  } catch (error) {
    logger.error(error, { message: 'Failed to complete order.' });
    const graphqlError = error.graphQLErrors?.[0] || {};

    const messageText = graphqlError.message || error.message;
    const message = (
      <span>
        {capitalizeFirst(messageText)}. Please review your order and resubmit or
        contact{' '}
        <Link theme={theme} href={`mailto:${SUPPORT_EMAIL}`}>
          {SUPPORT_EMAIL}
        </Link>{' '}
        for help.
      </span>
    );

    refreshOrder();
    setSubmitting(false);
    if (/creating payment method/i.test(messageText)) {
      setPaymentError(error);
      return;
    }

    createNotification({ message });
  }
}
